import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { AlertCircle, Send } from 'lucide-react';
import { SlashCommandMenu } from './SlashCommandMenu';
import type { SlashCommandDefinition } from '../chat-commands';
import { usePlayerActivityStore } from '@/lib/stores/playerActivityStore';

interface ChatComposerProps {
  value: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onSend: () => void;
  placeholder: string;
  isOnline: boolean;
  /** Stable id on the input element — lets scenario drivers target it without uid lookup */
  inputId?: string;
  /** Filtered slash commands to show in menu */
  slashCommands?: SlashCommandDefinition[];
  /** Currently selected command index */
  slashCommandIndex?: number;
  /** Callback when a command is selected */
  onSlashCommandSelect?: (command: SlashCommandDefinition) => void;
  /** Callback to close the slash command menu */
  onSlashCommandClose?: () => void;
  /** Stage label for multi-step game commands (e.g., "Select target") */
  slashStageLabel?: string | null;
  /** Whether the slash menu is actively filtering (shows empty state when no results) */
  slashMenuActive?: boolean;
  /** ID of the active (selected) menu item for aria-activedescendant */
  activeDescendantId?: string;
}

export function ChatComposer({
  value,
  inputRef,
  onChange,
  onKeyDown,
  onSend,
  placeholder,
  isOnline,
  inputId,
  slashCommands,
  slashCommandIndex = 0,
  onSlashCommandSelect,
  onSlashCommandClose,
  slashStageLabel,
  slashMenuActive,
  activeDescendantId,
}: ChatComposerProps) {
  const showMenu = Boolean(slashMenuActive || (slashCommands && slashCommands.length > 0));

  // Treat typing in the composer as a "player is here" signal — same store
  // the AFK timer reads in GameLayout / LobbyLayout. Pre-fix the timer only
  // bumped on outbound WS sends (markPlayerIntent in gameConnectionManager
  // .sendAction); during long NPC narration sequences the player could type
  // multiple drafts without committing and the timer would still fire,
  // kicking them mid-sentence (scenario 2026-05-12 B9). NPC narration scrolls
  // the chat panel but does NOT dispatch onChange on this input, so the
  // signal stays narrow: only the player's keystrokes in the composer count.
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    usePlayerActivityStore.getState().markPlayerIntent();
    onChange(event);
  };

  // Mirrors mobile's "/" composer button: makes slash commands discoverable
  // without knowing to type "/". The web slash menu is value-driven in both
  // lobby (activeMessage.startsWith('/')) and game (handleTextChange on "/"),
  // so "open" = push a "/" value through the existing onChange path. We don't
  // invent a new open route; both modes already react to the value.
  const handleSlashButtonClick = () => {
    if (showMenu) {
      onSlashCommandClose?.();
      return;
    }
    // The lobby/game onChange handlers only read e.target.value, so a minimal
    // synthetic event is safe and still fires markPlayerIntent via handleChange.
    handleChange({
      target: { value: '/' },
    } as React.ChangeEvent<HTMLInputElement>);
    inputRef.current?.focus();
  };

  return (
    <div className="flex gap-2 pt-2 items-center flex-shrink-0">
      <Button
        id={inputId ? `${inputId}-slash` : undefined}
        type="button"
        // SlashCommandMenu closes on any document mousedown outside its own
        // ref (SlashCommandMenu.tsx). This button sits outside that ref, so a
        // raw click would fire that close on mousedown, then re-open via the
        // onClick toggle below (showMenu already flipped false) — defeating
        // the close. Stop mousedown here so only the onClick toggle decides.
        onMouseDown={(e) => e.stopPropagation()}
        onClick={handleSlashButtonClick}
        size="icon"
        variant={showMenu ? 'default' : 'outline'}
        disabled={!isOnline}
        aria-label="Slash commands"
        aria-expanded={showMenu}
        className="flex-shrink-0 text-lg font-bold leading-none"
      >
        /
      </Button>
      <div className="flex-1 relative">
        {showMenu && onSlashCommandSelect && onSlashCommandClose && (
          <SlashCommandMenu
            commands={slashCommands ?? []}
            selectedIndex={slashCommandIndex}
            onSelect={onSlashCommandSelect}
            onClose={onSlashCommandClose}
            stageLabel={slashStageLabel}
            hasActiveFilter={slashMenuActive && (!slashCommands || slashCommands.length === 0)}
          />
        )}
        <Input
          ref={inputRef}
          id={inputId}
          value={value}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          enterKeyHint="send"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          className={`pr-8 ${!isOnline ? 'opacity-50' : ''}`}
          disabled={!isOnline}
          role="combobox"
          aria-expanded={showMenu}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={showMenu ? activeDescendantId : undefined}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isOnline ? (
            <div className="w-2 h-2 bg-green-500 rounded-full" title="Connected" />
          ) : (
            <span title="Disconnected">
              <AlertCircle className="w-4 h-4 text-orange-500" />
            </span>
          )}
        </div>
      </div>
      <Button
        id={inputId ? `${inputId}-send` : undefined}
        onClick={onSend}
        size="icon"
        className="flex-shrink-0"
        disabled={!value.trim() || !isOnline}
      >
        <Send className="h-4 w-4" />
      </Button>
    </div>
  );
}
